import { APP_STORE_URL, PLAY_STORE_URL } from "@/lib/constants";
import { useTranslations } from "@/contexts/I18nContext";

/**
 * "Get the Zoey app" banner shown on the signed-out auth screens (Login /
 * Register) via AuthLayout. Both native apps are live — iOS on the App Store
 * and Android on Google Play — so this nudges phone visitors toward the
 * native experience regardless of platform.
 *
 * Dark-mode safe — the auth screens honor the system theme. The store badges
 * stay canonical black pills in both themes (Apple's badge is always dark),
 * so they read as recognizable store badges rather than generic buttons.
 */
export function AppDownloadBanner() {
  const t = useTranslations();

  return (
    <div className="flex items-center justify-between gap-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-3">
      <div className="min-w-0">
        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          {t("auth.downloadApp.title")}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {t("auth.downloadApp.subtitle")}
        </p>
      </div>
      <div className="flex shrink-0 flex-col gap-2">
        <a
          href={APP_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t("auth.downloadApp.aria")}
          className="inline-flex items-center gap-2 rounded-xl bg-gray-900 px-3 py-2 text-white transition-colors hover:bg-black"
        >
          <svg className="h-6 w-6 shrink-0" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
          </svg>
          <span className="flex flex-col items-start leading-tight">
            <span className="text-[0.6rem] font-medium tracking-wide opacity-80">
              {t("auth.downloadApp.badgePrefix")}
            </span>
            <span className="-mt-0.5 text-sm font-semibold">
              {t("auth.downloadApp.badgeStore")}
            </span>
          </span>
        </a>
        <a
          href={PLAY_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t("auth.downloadApp.ariaPlay")}
          className="inline-flex items-center gap-2 rounded-xl bg-gray-900 px-3 py-2 text-white transition-colors hover:bg-black"
        >
          <svg className="h-6 w-6 shrink-0" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.199l2.302 2.302a1 1 0 010 1.38l-2.302 2.302L15.396 12l2.302-2.492zM5.864 2.658L16.8 8.99l-2.302 2.302L5.864 2.658z" />
          </svg>
          <span className="flex flex-col items-start leading-tight">
            <span className="text-[0.6rem] font-medium tracking-wide opacity-80">
              {t("auth.downloadApp.badgePrefixPlay")}
            </span>
            <span className="-mt-0.5 text-sm font-semibold">
              {t("auth.downloadApp.badgeStorePlay")}
            </span>
          </span>
        </a>
      </div>
    </div>
  );
}
